import { combineLatest, EMPTY, interval, Observable, of, Subject } from "rxjs";
import { catchError, debounceTime, filter, map, startWith, switchMap, take, takeWhile, tap } from "rxjs/operators";
import { PortfolioFacade } from "@modules/portfolio/domain/portfolio.facade";
import { inject, Injectable } from "@angular/core";
import { AccountService } from "@modules/account/services/account.service";
import { AdvTradingTrackingService } from "@modules/advanced-trading/services/adv-trading-tracking.service";
import { Router } from "@angular/router";
import { AdvTradingRemote } from "@modules/advanced-trading/infrastructure/adv-trading.remote";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AdvTradingProvisionerService } from "@modules/advanced-trading/services/adv-trading-provisioner.service";
import { UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { TaxesStateService, TaxesUserState } from "@modules/taxes/domain/+state/taxes.state.service";

export interface AdvTradingVm {
	userHasFunds: boolean;
	userHasAssetsButNoFiat: boolean;
	isLoading: boolean;
	showError: boolean;
	isUnconfirmed: boolean;
	userHasNothingToDeclare: boolean;
	userHasSomethingToDeclare: boolean;
	taxStateError: boolean;
}

interface TaxState {
	isUnconfirmed: boolean;
	userHasNothingToDeclare: boolean;
	userHasSomethingToDeclare: boolean;
	error: boolean;
}

@UntilDestroy()
@Injectable({
	providedIn: "root"
})
export class AdvancedTradingFacade {
	private readonly portfolioFacade = inject(PortfolioFacade);
	private readonly accountService = inject(AccountService);
	private readonly router = inject(Router);
	private readonly advTradingTrackingService = inject(AdvTradingTrackingService);
	private readonly remote = inject(AdvTradingRemote);
	private readonly advTradingProvisionerService = inject(AdvTradingProvisionerService);
	private readonly taxesStateService = inject(TaxesStateService);
	private readonly tCService = inject(TermsAndConditionsService);
	advanceTradingUserJourney: UserJourney;
	private advanceTradingUserJourney$ = new Subject<UserJourney>();
	advanceTradingBundles$ = this.advanceTradingUserJourney$.pipe(
		switchMap((userJourney: UserJourney) => {
			this.advanceTradingUserJourney = userJourney;
			return this.tCService.getBundlesRemote(userJourney);
		})
	);
	private readonly launchDarklyService = inject(LaunchDarklyService);
	public fusionSelfCertificationFF$ = this.launchDarklyService.isActive$(LD_FLAG.FUSION_SELF_CERTIFICATION);

	public userHasFunds$: Observable<boolean> = this.portfolioFacade.getPortfolioWidgetValues$().pipe(
		map((state) => {
			// user has many groups but the fiat group or the crypto one have balance
			if (state && state.portfolioData?.projection.groups.length >= 1) {
				return state.portfolioData.projection.groups.some(
					(group) => (group.isFiat || group.assetGroup.toLowerCase() === "crypto") && +group.fiatBalance > 0
				);
			}

			return false;
		})
	);

	public userHasAssetsButNoFiat$: Observable<boolean> = this.portfolioFacade.getPortfolioWidgetValues$().pipe(
		map((state) => {
			if (state && state.portfolioData?.projection.groups.length > 1) {
				const hasAssets = state.portfolioData.projection.groups.some((group) => !group.isFiat);
				const hasFiat = state.portfolioData.projection.groups
					.filter((group) => group.isFiat)
					.some((fiatAsset) => +fiatAsset.fiatBalance > 0);
				return hasAssets && !hasFiat;
			}
		})
	);

	public navigateToAdvancedTrading(trackingSource: string): void {
		this.advTradingTrackingService.bannerWasClicked(trackingSource);
		this.router.navigateByUrl("/fusion");
	}

	private checkIfUserIsProvisioned$ = this.remote.checkIfUserIsProvisioned$().pipe(
		debounceTime(100),
		map((response) => {
			this.advanceTradingUserJourney$.next(response.user_journey);
			return response.enabled;
		})
	);

	private provideUserToTalos$(): Observable<void> {
		return this.remote.provideUserToTalos$();
	}

	private startPollingUserCheck(): void {
		this.advTradingProvisionerService.setLoading(true);

		const polling$ = interval(500)
			.pipe(
				startWith(0),
				switchMap(() =>
					this.checkIfUserIsProvisioned$.pipe(
						catchError(() => {
							this.advTradingProvisionerService.setLoading(false);
							this.advTradingProvisionerService.setShowError(true);
							return of(false);
						})
					)
				),
				tap((provisioned) => {
					if (provisioned) {
						this.advTradingProvisionerService.setLoading(false);
					}
				}),
				takeWhile((provisioned, index) => !provisioned && index <= 20),
				untilDestroyed(this)
			)
			.pipe()
			.subscribe();

		setTimeout(() => {
			if (!polling$.closed) {
				this.advTradingProvisionerService.setLoading(false);
				this.advTradingProvisionerService.setShowError(true);
				polling$.unsubscribe();
			}
		}, 10000);
	}

	isCalled = false;

	public provideUserToTalosWhenNotProvidedYet(): void {
		// Modals are fired twice at the moment. A fix has been done. As soon as it is in master, this should be deleted
		if (this.isCalled) {
			return;
		}
		this.isCalled = true;
		this.advTradingProvisionerService.setLoading(true);
		this.checkIfUserIsProvisioned$
			.pipe(
				take(1),
				untilDestroyed(this),
				catchError(() => {
					this.advTradingProvisionerService.setLoading(false);
					this.advTradingProvisionerService.setShowError(true);
					return of(false);
				}),
				tap(() => (this.isCalled = false)),
				tap((isProvisioned) => {
					if (isProvisioned) {
						this.advTradingProvisionerService.setLoading(false);
					}
				}),
				filter((isProvisioned) => !isProvisioned),
				switchMap(() => this.provideUserToTalos$()),
				catchError(() => {
					this.advTradingProvisionerService.setLoading(false);
					this.advTradingProvisionerService.setShowError(true);
					return of(EMPTY);
				}),
				filter((response) => response !== EMPTY)
			)
			.subscribe(() => {
				this.startPollingUserCheck();
			});
	}

	private readonly defaultState: TaxState = {
		isUnconfirmed: false,
		userHasNothingToDeclare: false,
		userHasSomethingToDeclare: false,
		error: false
	};

	private readonly taxState$ = this.fusionSelfCertificationFF$.pipe(
		switchMap((isAvailable) => (!isAvailable ? of(this.defaultState) : this.getTaxStatus()))
	);

	private getTaxStatus(): Observable<TaxState> {
		return this.taxesStateService.getTaxesState$().pipe(
			map((taxStatus: TaxesUserState | null) => {
				if (!taxStatus) {
					return this.defaultState;
				}

				const status = taxStatus.taxStatus.toLowerCase();
				const openDeclarationsCount = taxStatus.declarationsOpen;

				if (taxStatus.hasError) {
					return { ...this.defaultState, error: true };
				}

				switch (status) {
					case "unconfirmed":
						return { ...this.defaultState, isUnconfirmed: true };
					case "eligible":
						return openDeclarationsCount === 0
							? { ...this.defaultState, userHasNothingToDeclare: true }
							: { ...this.defaultState, userHasSomethingToDeclare: true };
					default:
						return this.defaultState;
				}
			})
		);
	}

	private readonly derivedStates$ = {
		isUnconfirmed: this.taxState$.pipe(map(({ isUnconfirmed }) => isUnconfirmed)),
		userHasNothingToDeclare: this.taxState$.pipe(map(({ userHasNothingToDeclare }) => userHasNothingToDeclare)),
		userHasSomethingToDeclare: this.taxState$.pipe(
			map(({ userHasSomethingToDeclare }) => userHasSomethingToDeclare)
		),
		taxStateError: this.taxState$.pipe(map(({ error }) => error))
	};

	public getAdvTradingVm$(): Observable<AdvTradingVm> {
		return combineLatest({
			userHasFunds: this.userHasFunds$,
			userHasAssetsButNoFiat: this.userHasAssetsButNoFiat$,
			isLoading: this.advTradingProvisionerService.isLoading$,
			showError: this.advTradingProvisionerService.showError$,
			...this.derivedStates$
		});
	}
}
